import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { ethers } from 'ethers6';
import { useAccount, useWalletClient } from 'wagmi';
import { uploadBlobToStorage, STORAGE_BUCKETS, createETHToken } from '../../utils/supabase';
import { useNavigate } from 'react-router-dom';
import { ETH_COLORS } from './ETHLayout';
import { Upload, Plus, Globe, Twitter, Send, MessageCircle, Loader2, CheckCircle, ExternalLink } from 'lucide-react';
// Gas sponsorship disabled - using regular deployment
import { config } from '../../utils/config';

// ============================================
// ETH CREATE TOKEN PAGE
// Deploy tokens paired with USDC or WETH on Ethereum Mainnet
// Uses MavrkTokenFactory V4 Contract
// ============================================

// V4 Factory Contract on Ethereum Mainnet
const FACTORY_ADDRESS_ETH = '0x25CA68F2C3d6c26623670C93426d74C901068bD6';

// Base Token Addresses
const USDC_BASE_TOKEN = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
const WETH_BASE_TOKEN = '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2';

// Uniswap V3 NPM Address on Ethereum Mainnet
const UNISWAP_NPM = '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';

// V4 Factory ABI (newMavrkToken with baseToken parameter)
const FACTORY_V4_ABI = [
  {
    "inputs": [
      { "internalType": "string", "name": "_name", "type": "string" },
      { "internalType": "string", "name": "_symbol", "type": "string" },
      { "internalType": "address", "name": "npm", "type": "address" },
      { "internalType": "address", "name": "baseToken", "type": "address" }
    ],
    "name": "newMavrkToken",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "anonymous": false,
    "inputs": [
      { "indexed": true, "internalType": "address", "name": "tokenAddress", "type": "address" },
      { "indexed": false, "internalType": "string", "name": "name", "type": "string" },
      { "indexed": false, "internalType": "string", "name": "symbol", "type": "string" },
      { "indexed": true, "internalType": "address", "name": "creator", "type": "address" },
      { "indexed": true, "internalType": "address", "name": "npm", "type": "address" },
      { "indexed": false, "internalType": "uint256", "name": "totalDeployed", "type": "uint256" }
    ],
    "name": "TokenDeployed",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      { "indexed": false, "internalType": "address", "name": "pool", "type": "address" },
      { "indexed": false, "internalType": "address", "name": "npm", "type": "address" }
    ],
    "name": "PoolInitialized",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      { "indexed": false, "internalType": "uint256", "name": "tokenId", "type": "uint256" },
      { "indexed": true, "internalType": "address", "name": "creator", "type": "address" },
      { "indexed": false, "internalType": "address", "name": "npm", "type": "address" }
    ],
    "name": "LiquidityMinted",
    "type": "event"
  }
];

// ============ ANIMATIONS ============
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ============ STYLED COMPONENTS ============
const PageContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
  text-align: center;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 32px 0;
  text-align: center;
`;

const FormCard = styled.div`
  background: transparent;
  border: 1px solid ${ETH_COLORS.border};
  border-radius: 16px;
  padding: 28px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${ETH_COLORS.lightBlue};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  
  &.required::after {
    content: ' *';
    color: ${ETH_COLORS.lightBlue};
  }
`;

const Input = styled.input`
  padding: 12px 16px;
  background: transparent;
  border: 1px solid ${ETH_COLORS.borderLight};
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 0.15s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    border-color: ${ETH_COLORS.border};
    box-shadow: 0 0 0 3px rgba(57, 98, 116, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  background: transparent;
  border: 1px solid ${ETH_COLORS.borderLight};
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 0.15s ease;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  &:focus {
    border-color: ${ETH_COLORS.border};
    box-shadow: 0 0 0 3px rgba(57, 98, 116, 0.15);
  }
`;

// ============ LOGO UPLOAD ============
const LogoUploadArea = styled.div<{ $hasImage?: boolean }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: transparent;
  border: 2px dashed ${props => props.$hasImage ? ETH_COLORS.border : ETH_COLORS.borderLight};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  
  &:hover {
    border-color: ${ETH_COLORS.borderHover};
    background: rgba(57, 98, 116, 0.1);
  }
`;

const LogoPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadIcon = styled.div`
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 8px;
`;

const UploadText = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
`;


// ============ CATEGORY SELECTOR ============
const CategoryGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CategoryButton = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  background: ${props => props.$active 
    ? 'rgba(57, 98, 116, 0.2)' 
    : 'transparent'};
  border: 1px solid ${props => props.$active 
    ? ETH_COLORS.border
    : ETH_COLORS.borderLight};
  border-radius: 20px;
  color: ${props => props.$active ? ETH_COLORS.lightBlue : 'rgba(255, 255, 255, 0.6)'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(57, 98, 116, 0.15);
    color: ${ETH_COLORS.lightBlue};
  }
`;

// ============ DEPLOY BUTTON ============
const DeployButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 16px 24px;
  background: ${props => props.$disabled 
    ? 'rgba(57, 98, 116, 0.2)' 
    : ETH_COLORS.accentGradient};
  border: none;
  border-radius: 12px;
  color: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.4)' : ETH_COLORS.accent};
  font-size: 16px;
  font-weight: 700;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
  
  &:hover {
    ${props => !props.$disabled && `
      transform: translateY(-2px);
      box-shadow: 0 4px 20px rgba(57, 98, 116, 0.4);
    `}
  }
`;

const SpinnerIcon = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

// ============ STATUS MESSAGE ============
const StatusMessage = styled.div<{ $type: 'info' | 'success' | 'error' }>`
  padding: 16px;
  border-radius: 10px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  
  ${props => {
    switch (props.$type) {
      case 'success':
        return `
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: #10b981;
        `;
      case 'error':
        return `
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
        `;
      default:
        return `
          background: rgba(57, 98, 116, 0.15);
          border: 1px solid ${ETH_COLORS.border};
          color: ${ETH_COLORS.lightBlue};
        `;
    }
  }}
`;

const SuccessLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const SuccessLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(57, 98, 116, 0.15);
  border: 1px solid ${ETH_COLORS.border};
  border-radius: 8px;
  color: ${ETH_COLORS.lightBlue};
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(57, 98, 116, 0.25);
    color: #ffffff;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

// ============ CATEGORIES ============
const CATEGORIES = [
  'Meme',
  'DeFi',
  'Utility',
  'Gaming',
  'AI & Tech',
  'NFT',
  'DAO',
  'Other'
];

// ============ COMPONENT ============
const ETHCreateTokenPage: React.FC = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const logoInputRef = useRef<HTMLInputElement>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    description: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: '',
    category: 'Meme',
    baseToken: 'USDC' as 'USDC' | 'WETH', // Base token selection
  });
  
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployStatus, setDeployStatus] = useState<{
    type: 'info' | 'success' | 'error';
    message: string;
    tokenAddress?: string;
    txHash?: string;
  } | null>(null);
  
  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle symbol change (uppercase, alphanumeric only)
  const handleSymbolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setFormData(prev => ({ ...prev, symbol: value }));
  };
  
  // Handle logo upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Deploy token using regular transaction (gas sponsorship disabled)
  const handleDeploy = async () => {
    if (!isConnected || !walletClient || !address) {
      setDeployStatus({
        type: 'error',
        message: 'Please connect your wallet to deploy a token.'
      });
      return;
    }
    
    if (!formData.name || !formData.symbol) {
      setDeployStatus({
        type: 'error',
        message: 'Please fill in the token name and symbol.'
      });
      return;
    }
    
    setIsDeploying(true);
    setDeployStatus({
      type: 'info',
      message: 'Preparing deployment...'
    });
    
    try {
      // Create provider and signer
      const provider = new ethers.BrowserProvider(walletClient);
      const signer = await provider.getSigner();
      const network = await provider.getNetwork();
      
      if (network.chainId !== BigInt(config.ethereum.chainId)) {
        setDeployStatus({
          type: 'error',
          message: `Please switch your wallet to Ethereum Mainnet (Chain ID: ${config.ethereum.chainId}) before deploying.`
        });
        setIsDeploying(false);
        return;
      }
      
      // Get selected base token address
      const baseTokenAddress = formData.baseToken === 'USDC' ? USDC_BASE_TOKEN : WETH_BASE_TOKEN;
      
      setDeployStatus({
        type: 'info',
        message: 'Sending transaction... Please confirm in your wallet.'
      });
      
      // Create factory contract instance
      const factory = new ethers.Contract(FACTORY_ADDRESS_ETH, FACTORY_V4_ABI, signer);
      
      // Deploy token using regular transaction
      const tx = await factory.newMavrkToken(
        formData.name,
        formData.symbol,
        UNISWAP_NPM,
        baseTokenAddress
      );
      
      setDeployStatus({
        type: 'info',
        message: 'Transaction sent! Waiting for confirmation...'
      });
      
      // Wait for transaction to be mined
      const receipt = await tx.wait();
      
      if (!receipt) {
        throw new Error('Failed to fetch transaction receipt');
      }
      
      setDeployStatus({
        type: 'info',
        message: 'Transaction confirmed! Parsing events...'
      });
      
      console.log('Deployment receipt:', receipt);
      
      // Parse events to get token address
      const factoryContract = new ethers.Contract(FACTORY_ADDRESS_ETH, FACTORY_V4_ABI, provider);
      let tokenAddress = '';
      let poolAddress = '';
      let nftTokenId = '';
      
      for (const log of receipt.logs) {
        try {
          const parsed = factoryContract.interface.parseLog({
            topics: log.topics as string[],
            data: log.data
          });
          
          if (parsed?.name === 'TokenDeployed') {
            tokenAddress = parsed.args.tokenAddress;
            console.log('Token deployed:', tokenAddress);
          }
          if (parsed?.name === 'PoolInitialized') {
            poolAddress = parsed.args.pool;
            console.log('Pool initialized:', poolAddress);
          }
          if (parsed?.name === 'LiquidityMinted') {
            nftTokenId = parsed.args.tokenId.toString();
            console.log('LP NFT minted:', nftTokenId);
          }
        } catch (e) {
          // Not a factory event, skip
        }
      }
      
      if (!tokenAddress) {
        throw new Error('Could not find TokenDeployed event in transaction receipt');
      }
      
      // Upload logo to Supabase (if provided)
      let logoUrl: string | null = null;
      if (logoFile) {
        setDeployStatus({
          type: 'info',
          message: 'Uploading logo...'
        });
        
        const logoFileName = `${tokenAddress.toLowerCase()}_logo.png`;
        logoUrl = await uploadBlobToStorage(logoFile, STORAGE_BUCKETS.ETH_LOGOS, logoFileName);
      }
      
      // Save to Supabase tokens_eth table
      setDeployStatus({
        type: 'info',
        message: 'Saving token data...'
      });
      
      const result = await createETHToken({
        name: formData.name,
        symbol: formData.symbol,
        contract_address: tokenAddress.toLowerCase(),
        deployer_address: address.toLowerCase(),
        pool_address: poolAddress || undefined,
        nft_token_id: nftTokenId || undefined,
        logo_url: logoUrl || undefined,
        description: formData.description || undefined,
        website: formData.website || undefined,
        twitter: formData.twitter || undefined,
        telegram: formData.telegram || undefined,
        discord: formData.discord || undefined,
        category: formData.category,
        base_token: formData.baseToken, // Selected base token (USDC or WETH)
        dex: 'Uniswap V3',
        tx_hash: receipt.hash,
      });
      
      if (!result.success) {
        console.warn('Failed to save to database:', result.error);
        // Don't fail the whole deployment for this
      }
      
      setDeployStatus({
        type: 'success',
        message: `${formData.name} ($${formData.symbol}) deployed successfully!`,
        tokenAddress,
        txHash: receipt.hash,
      });
      
    } catch (error: any) {
      console.error('Deployment error:', error);
      setDeployStatus({
        type: 'error',
        message: error.reason || error.message || 'Deployment failed. Please try again.'
      });
    } finally {
      setIsDeploying(false);
    }
  };
  
  // Only name, symbol, and wallet connection are required
  const isFormValid = formData.name && formData.symbol && isConnected;
  
  return (
    <PageContainer>
      <PageTitle>Create Token</PageTitle>
      <PageSubtitle>
        Launch your token on Ethereum with liquidity pairing
      </PageSubtitle>
      
      <FormCard>
        {/* Token Info */}
        <FormSection>
          <SectionTitle>
            <Plus size={16} /> Token Information
          </SectionTitle>
          
          <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <LogoUploadArea 
                $hasImage={!!logoPreview}
                onClick={() => logoInputRef.current?.click()}
              >
                {logoPreview ? (
                  <LogoPreview src={logoPreview} alt="Token logo" />
                ) : (
                  <>
                    <UploadIcon><Upload size={24} /></UploadIcon>
                    <UploadText>Upload Logo</UploadText>
                  </>
                )}
              </LogoUploadArea>
              <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.4)' }}>Optional</span>
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={handleLogoUpload}
              style={{ display: 'none' }}
            />
            
            <div style={{ flex: 1 }}>
              <FormRow>
                <FormGroup>
                  <Label className="required">Token Name</Label>
                  <Input
                    name="name"
                    placeholder="e.g. Liberty Token"
                    value={formData.name}
                    onChange={handleInputChange}
                    maxLength={50}
                  />
                </FormGroup>
                <FormGroup>
                  <Label className="required">Symbol</Label>
                  <Input
                    name="symbol"
                    placeholder="e.g. LBTK"
                    value={formData.symbol}
                    onChange={handleSymbolChange}
                    maxLength={10}
                  />
                </FormGroup>
              </FormRow>
            </div>
          </div>
          
          <FormGroup>
            <Label>Description</Label>
            <TextArea
              name="description"
              placeholder="Tell us about your token..."
              value={formData.description}
              onChange={handleInputChange}
              maxLength={500}
            />
          </FormGroup>
        </FormSection>
        
        {/* Base Token Selection */}
        <FormSection>
          <SectionTitle>Base Pair Selection</SectionTitle>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr',
            gap: '12px'
          }}>
            {/* USDC Option */}
            <div 
              onClick={() => setFormData(prev => ({ ...prev, baseToken: 'USDC' }))}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                padding: '16px',
                background: formData.baseToken === 'USDC' 
                  ? 'rgba(57, 98, 116, 0.2)' 
                  : 'rgba(57, 98, 116, 0.1)',
                borderRadius: '12px',
                border: formData.baseToken === 'USDC'
                  ? `2px solid ${ETH_COLORS.border}`
                  : `1px solid ${ETH_COLORS.borderLight}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <img src="/usd-coin-usdc-logo.png" alt="USDC" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#ffffff' }}>Paired with USDC</div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                  Launch with USDC liquidity on Uniswap V3
                </div>
              </div>
              {formData.baseToken === 'USDC' && (
                <CheckCircle size={20} color={ETH_COLORS.lightBlue} />
              )}
            </div>
            
            {/* WETH Option */}
            <div 
              onClick={() => setFormData(prev => ({ ...prev, baseToken: 'WETH' }))}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                padding: '16px',
                background: formData.baseToken === 'WETH' 
                  ? 'rgba(57, 98, 116, 0.2)' 
                  : 'rgba(57, 98, 116, 0.1)',
                borderRadius: '12px',
                border: formData.baseToken === 'WETH'
                  ? `2px solid ${ETH_COLORS.border}`
                  : `1px solid ${ETH_COLORS.borderLight}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <img src="/landing-assets/ETH_logo.png" alt="WETH" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#ffffff' }}>Paired with WETH</div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                  Launch with WETH liquidity on Uniswap V3
                </div>
              </div>
              {formData.baseToken === 'WETH' && (
                <CheckCircle size={20} color={ETH_COLORS.lightBlue} />
              )}
            </div>
          </div>
        </FormSection>
        
        {/* Category */}
        <FormSection>
          <SectionTitle>Category</SectionTitle>
          <CategoryGrid>
            {CATEGORIES.map((cat) => (
              <CategoryButton
                key={cat}
                $active={formData.category === cat}
                onClick={() => setFormData(prev => ({ ...prev, category: cat }))}
              >
                {cat}
              </CategoryButton>
            ))}
          </CategoryGrid>
        </FormSection>
        
        {/* Socials */}
        <FormSection>
          <SectionTitle>
            <Globe size={16} /> Socials (Optional)
          </SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Website</Label>
              <Input
                name="website"
                placeholder="https://yourproject.com"
                value={formData.website}
                onChange={handleInputChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Twitter / X</Label>
              <Input
                name="twitter"
                placeholder="https://x.com/yourproject"
                value={formData.twitter}
                onChange={handleInputChange}
              />
            </FormGroup>
          </FormRow>
          <FormRow style={{ marginTop: '12px' }}>
            <FormGroup>
              <Label>Telegram</Label>
              <Input
                name="telegram"
                placeholder="https://t.me/yourproject"
                value={formData.telegram}
                onChange={handleInputChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Discord</Label>
              <Input
                name="discord"
                placeholder="https://discord.gg/yourproject"
                value={formData.discord}
                onChange={handleInputChange}
              />
            </FormGroup>
          </FormRow>
        </FormSection>
        
        {/* Deploy Button */}
        <DeployButton
          $disabled={!isFormValid || isDeploying}
          onClick={handleDeploy}
          disabled={!isFormValid || isDeploying}
        >
          {isDeploying ? (
            <>
              <SpinnerIcon size={20} />
              Deploying...
            </>
          ) : !isConnected ? (
            'Connect Wallet to Deploy'
          ) : (
            <>
              <Plus size={20} />
              Deploy Token
            </>
          )}
        </DeployButton>
        
        {/* Status Message */}
        {deployStatus && (
          <StatusMessage $type={deployStatus.type}>
            {deployStatus.type === 'success' && <CheckCircle size={20} />}
            {deployStatus.type === 'info' && <Loader2 size={20} className="animate-spin" />}
            <div>
              {deployStatus.message}
              {deployStatus.type === 'success' && deployStatus.tokenAddress && (
                <SuccessLinks>
                  <SuccessLink
                    href={`https://etherscan.io/token/${deployStatus.tokenAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink /> View on Etherscan
                  </SuccessLink>
                  <SuccessLink
                    href={`/app/eth/trade`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/app/eth/trade');
                    }}
                  >
                    <ExternalLink /> View on ETH
                  </SuccessLink>
                </SuccessLinks>
              )}
            </div>
          </StatusMessage>
        )}
      </FormCard>
    </PageContainer>
  );
};

export default ETHCreateTokenPage;

